import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { api, type AnswerSurveyResult, type ApiError, type OpenSurveyResult } from '@whinerp/client';
import { Button } from '@whinerp/ui';
import { useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';

/**
 * SC-82 A satisfaction survey (G-197): one question on its own scale, a
 * comment, answered once.
 */

const SCALE = { csat: [1, 2, 3, 4, 5], nps: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10], ces: [1, 2, 3, 4, 5, 6, 7] } as const;

function SurveyScreen(): JSX.Element {
  const t = useT();
  const token = useParams()['token'] ?? '';
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const survey = useQuery<OpenSurveyResult, ApiError>({ queryKey: ['survey', token], queryFn: () => api.openSurvey({ body: { token } }) });
  const answer = useMutation<AnswerSurveyResult, ApiError>({ mutationFn: () => api.answerSurvey({ body: { token, score: score!, comment: comment || null } }) });

  if (survey.isPending) return <SurveyLoading />;
  const view = survey.data?.survey;
  if (!view) return <SurveyMissing />;
  const done = answer.data?.answered || answer.data?.reason === 'already' || view.answered;

  return (
    <main data-component="Survey" className="mx-auto flex min-h-screen w-full max-w-reading flex-col gap-4 px-gutter py-6">
      <p className="text-meta text-ink-meta">{view.caseNumber} · {view.subject}</p>
      {done ? (
        <p data-survey-thanks className="text-h2 text-ink">{answer.data?.answered ? t('survey.thanks') : t('survey.already')}</p>
      ) : (
        <form data-survey-form className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (score !== null) answer.mutate();
          }}>
          <h1 className="text-h2 text-ink">{t(`survey.question.${view.kind}`)}</h1>
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t(`survey.question.${view.kind}`)}>
            {SCALE[view.kind].map((value) => (
              <Button key={value} size="small" intent={score === value ? 'primary' : 'secondary'} data-survey-score={value}
                aria-pressed={score === value} onClick={() => setScore(value)}>
                {String(value)}
              </Button>
            ))}
          </div>
          <p className="text-meta text-ink-meta">{t(`survey.scale.${view.kind}`)}</p>
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('survey.comment')}
            <textarea className="min-h-24 rounded-control border border-control bg-surface p-3 text-body text-ink" data-survey-comment maxLength={2000}
              value={comment} onChange={(event) => setComment(event.target.value)} />
          </label>
          {answer.data?.reason === 'expired' ? <p className="text-dense text-danger-text">{t('survey.missing')}</p> : null}
          <div>
            <Button intent="primary" type="submit" data-survey-send disabled={score === null || answer.isPending}>{t('survey.send')}</Button>
          </div>
        </form>
      )}
    </main>
  );
}

function SurveyLoading(): JSX.Element {
  const t = useT();
  return <p role="status" className="p-6 text-body text-ink-meta">{t('state.loading')}</p>;
}
function SurveyMissing(): JSX.Element {
  const t = useT();
  return <p data-survey-missing className="p-6 text-body text-ink-meta">{t('survey.missing')}</p>;
}

export const screen = defineScreen({
  id: 'SC-82',
  route: '/survey/:token',
  surface: 'page',
  chrome: 'bare',
  title: 'Satisfaction survey',
  features: ['F-085'],
  entitlement: 'always-on',
  reachedBy: [{ kind: 'public' }],
  components: [],
  elements: ['Survey'],
  states: { empty: SurveyMissing, loading: SurveyLoading, error: SurveyMissing },
  component: SurveyScreen,
});
